import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A pasted screenshot, shown at once while it uploads (screenshot speed,
 * Option A, Kyu 4 Oct; the Alex Huddle already does this,
 * HuddleCommentEditor.tsx insertUpload).
 *
 * The preview is a blob: URL and it is rendered BESIDE the editor, never in
 * its content. A comment body is saved as the editor's HTML, and a blob:
 * picture there would be saved and then vanish for every reader. Only the
 * Drive link the upload returns goes into the document.
 *
 * Shared by the comment box (CommentEditor) and the supplier thread editor
 * (RichTextEditor), so both behave the same.
 */
export function usePasteUploadPreview(onUploadingChange?: (uploading: boolean) => void) {
  const [preview, setPreview] = useState<string | null>(null);
  // How many uploads are running. Read through a ref by keymaps that are
  // built once and so cannot see state.
  const running = useRef(0);
  const previewRef = useRef<string | null>(null);
  const reportRef = useRef(onUploadingChange);
  reportRef.current = onUploadingChange;

  useEffect(
    () => () => {
      if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    },
    [],
  );

  const track = useCallback(async <T,>(file: File, run: () => Promise<T>): Promise<T> => {
    const url = URL.createObjectURL(file);
    if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    previewRef.current = url;
    setPreview(url);
    running.current += 1;
    if (running.current === 1) reportRef.current?.(true);
    try {
      return await run();
    } finally {
      running.current -= 1;
      if (running.current === 0) reportRef.current?.(false);
      URL.revokeObjectURL(url);
      if (previewRef.current === url) {
        previewRef.current = null;
        setPreview(null);
      }
    }
  }, []);

  return { preview, track, isUploading: () => running.current > 0 };
}

/** The preview itself: the same look as the Huddle's (huddle-upload-preview). */
export function PasteUploadPreview({ src }: { src: string | null }) {
  if (src === null) return null;
  return (
    <div className="paste-upload-preview" role="status" aria-label="Screenshot uploading">
      <img src={src} alt="Screenshot preview" />
      <span>Uploading…</span>
    </div>
  );
}
